import { IOutfitsData, IOutfitsAction } from 'models/outfit/outfits';
import { IStore } from 'redux/IStore';
import { IPage } from 'models/common';
import { IUserPreferences } from 'models/user/userpreferences';
import { IOutfitListItem } from '../../../../models/outfit/outfitlistitem';
import { Likes } from 'redux/modules/user/likes';
import { LikeNodeType } from 'models/enums/nodetype/likenodetype';

/** Action Types */
export const REQUEST_START: string = 'outfits/REQUEST_START';
export const REQUEST_FAILURE: string = 'outfits/REQUEST_FAILURE';
export const GET_SUCCESS: string = 'outfits/GET_SUCCESS';
export const RESET_DATA: string = 'outfits/RESET_DATA';
export const SET_USER_ID: string = 'outfits/SET_USER_ID';
export const SET_ITEMS_PAGE: string = 'outfits/SET_ITEMS_PAGE';
export const RESET_FILTERS: string = 'outfits/RESET_FILTERS';
export const SET_SORT_FILTER: string = 'outfits/SET_SORT_FILTER';
export const SET_ISFIRSTLOAD: string = 'outfits/SET_ISFIRSTLOAD';
export const LIKE_ITEM_SUCCESS: string = 'outfits/LIKE_ITEM_SUCCESS';
export const LIKE_REQUEST_START: string = 'outfits/LIKE_REQUEST_START';
export const LIKE_REQUEST_FAILURE: string = 'outfits/LIKE_REQUEST_FAILURE';

/** Initial State */
const initialState: IOutfitsData = {
	items: [],
	isFetching: false,
	itemsPage: {
		currentPage: 0,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: 10,
		items: [],
	},
	userId: null,
	sortFilter: null,
	isFirstLoad: false,
	isFetchingLikes: false,
};

/** Reducer */
export function outfitsReducer(state = initialState, action: IOutfitsAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: 10,
					items: [],
				},
				userId: null,
			});

		case SET_USER_ID:
			return Object.assign({}, state, {
				userId: action.payload.userId,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case RESET_FILTERS:
			return Object.assign({}, state, {
				sortFilter: null,
			});

		case SET_SORT_FILTER:
			return Object.assign({}, state, {
				sortFilter: action.payload.sortFilter,
				isFirstLoad: true,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		case LIKE_REQUEST_START:
			return Object.assign({}, state, {
				isFetchingLikes: true,
			});

		case LIKE_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetchingLikes: false,
			});

		case LIKE_REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetchingLikes: false,
				message: action.payload.message,
				error: true,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getUserOutfits(id: string, isFirstLoad: boolean) {
	return (dispatch, getState: () => IStore) => {
		if (isFirstLoad) {
			dispatch(resetOutfitsData());
			dispatch(setOutfitsIsFirstLoad(false));
		}

		if (id !== null) {
			dispatch(setUserId(id));
		}

		dispatch(requestStart());

		const currentPage: number = getState().outfits.itemsPage.currentPage;
		const newPage: number = currentPage + 1;
		const itemsPerPage: number = getState().outfits.itemsPage.itemsPerPage;
		const userPreferences: IUserPreferences = getState().userPreferences;

		let queryString = '?';
		queryString = queryString + '&currentPage=' + newPage;
		queryString = queryString + '&itemsPerPage=' + itemsPerPage;
		queryString = queryString + '&sortColumn=' + mapOutfitItemsSort(getState().outfits.sortFilter);

		const userId: string = id === null ? getState().outfits.userId : id;

		if (userId === userPreferences.id) {
			queryString = queryString + '&publishedOnly=false';
		}

		return fetch(
			getState().fetchDefault.baseAPI + 'api/outfit/GetByUser/' + userId + queryString,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						if (isFirstLoad) {
							dispatch(resetOutfitsData());
						}
						const outfitsState: IOutfitsData = getState().outfits;
						const items: IOutfitListItem[] = outfitsState.items;
						const newItems: IOutfitListItem[] = res.items;
						Array.prototype.push.apply(items, newItems);
						dispatch(getOutfitsSuccess(items));
						dispatch(setOutfitItemsPage(res));
						const likes = new Likes(LikeNodeType.Outfit);
						if (userPreferences && userPreferences.id) {
							dispatch(likes.getLikedItems());
						} else {
							dispatch(likes.updateLikeItems(null));
						}
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function addNewOutfitToList(outfit: IOutfitListItem) {
	return (dispatch, getState: () => IStore) => {
		const outfits: IOutfitListItem[] = getState().outfits.items;
		outfits.push(outfit);
		dispatch(getOutfitsSuccess(outfits));
	};
}

function mapOutfitItemsSort(sort: number): string {
	let mappedSort = 'Name ASC';
	switch (sort) {
		case 0:
			mappedSort = 'AddedDate DESC';
			break;
		case 1:
			mappedSort = 'AddedDate ASC';
			break;
		default:
			mappedSort = 'Name ASC';
	}
	return mappedSort;
}

/** Action Creator */
export function requestStart(): IOutfitsAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getOutfitsSuccess(outfits: IOutfitListItem[]): IOutfitsAction {
	return {
		type: GET_SUCCESS,
		payload: {
			items: outfits,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IOutfitsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function resetOutfitsData(): IOutfitsAction {
	return {
		type: RESET_DATA,
	};
}

export function resetOutfitsFilters(): IOutfitsAction {
	return {
		type: RESET_FILTERS,
	};
}

/** Action Creator */
export function setOutfitItemsPage(itemsPage: IPage): IOutfitsAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

export function setOutfitsSortFilter(sortFilter: number): IOutfitsAction {
	return {
		type: SET_SORT_FILTER,
		payload: {
			sortFilter,
		},
	};
}

export function setOutfitsIsFirstLoad(isFirstLoad: boolean): IOutfitsAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}

/** Action Creator */
export function setUserId(userId: string): IOutfitsAction {
	return {
		type: SET_USER_ID,
		payload: {
			userId,
		},
	};
}

/** Action Creator */
export function likeItemSuccess(): IOutfitsAction {
	return {
		type: LIKE_ITEM_SUCCESS,
	};
}

/** Action Creator */
export function likeRequestStart(): IOutfitsAction {
	return {
		type: LIKE_REQUEST_START,
	};
}

/** Action Creator */
export function likeRequestFailure(message: any): IOutfitsAction {
	return {
		type: LIKE_REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}
